import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  useMediaQuery,
  Stack,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Link } from "react-router-dom";
import { fetchWithAuth } from "../../utils/api";

const AdminEditSingleItem = () => {
  const [itemName, setItemName] = useState("");
  const [itemData, setItemData] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleSearch = async () => {
    const name = itemName.trim();
    if (!name || isSearching) return;

    setIsSearching(true);
    setFeedback(null);
    setItemData(null);
    setEditedData({});
    try {
      const data = await fetchWithAuth("/items/details", {
        method: "POST",
        body: { names: [name] },
      });
      const item = data.items?.[0];
      if (!item) {
        setFeedback({ severity: "warning", message: `No item found for "${name}".` });
        return;
      }

      setItemData(item);
      setEditedData({
        name: item.name,
        current_value_min: item.current_value_min,
        current_value_max: item.current_value_max,
        notes: item.notes || "",
      });
    } catch {
      setFeedback({ severity: "error", message: "Failed to fetch item details." });
    } finally {
      setIsSearching(false);
    }
  };

  const handleConfirmSave = async () => {
    if (!itemData || !isValid() || isSaving) return;

    setIsSaving(true);
    setFeedback(null);
    try {
      await fetchWithAuth("/items/bulk-update", {
        method: "PUT",
        body: {
          updates: [{
            id: itemData.id,
            name: editedData.name.trim(),
            current_value_min: Number(editedData.current_value_min),
            current_value_max: Number(editedData.current_value_max),
            is_stable: itemData.is_stable,
            is_volatile: itemData.is_volatile,
            type: itemData.type,
            notes: editedData.notes,
          }],
        },
      });
      setConfirmOpen(false);
      setItemData(null);
      setEditedData({});
      setItemName("");
      setFeedback({ severity: "success", message: "Item updated." });
    } catch {
      setConfirmOpen(false);
      setFeedback({ severity: "error", message: "Failed to save changes." });
    } finally {
      setIsSaving(false);
    }
  };

  const isValid = () => {
    const min = Number(editedData.current_value_min);
    const max = Number(editedData.current_value_max);
    return (
      editedData.name?.trim().length > 0 &&
      Number.isInteger(min) &&
      Number.isInteger(max) &&
      min > 0 &&
      max > 0 &&
      min <= max
    );
  };

  return (
    <>
      <div className="admin-container">
        <div className="admin-content">
            <Box p={2} sx={{ width: `${isMobile ? '100vw' : '70vw'}`, margin: '0 auto' }}>
              <Typography variant="h6" gutterBottom>Admin Edit - Single Item</Typography>

              {feedback && <Alert severity={feedback.severity} sx={{ mb: 2 }}>{feedback.message}</Alert>}

              {!isMobile && (
                <Box mb={2}>
                  <Button
                    component={Link}
                    to="/admin/editItems"
                    variant="outlined"
                    sx={{ textTransform: 'none' }}
                  >
                    ← Switch to Table Editor
                  </Button>
                </Box>
              )}

              <Stack direction="row" spacing={1} mb={2}>
                <TextField
                  fullWidth
                  label="Item Name"
                  value={itemName}
                  onChange={(e) => setItemName(e.target.value)}
                />
                <Button variant="contained" onClick={handleSearch} disabled={!itemName.trim() || isSearching}>
                  Search
                </Button>
              </Stack>

              {itemData && (
                <Box component="form" display="flex" flexDirection="column" gap={2}>
                  <TextField
                    label="Item Name"
                    value={editedData.name}
                    onChange={(e) => setEditedData((prev) => ({ ...prev, name: e.target.value }))}
                  />
                  <TextField
                    label="Value Min"
                    type="number"
                    inputProps={{ min: 1 }}
                    value={editedData.current_value_min}
                    onChange={(e) => setEditedData((prev) => ({ ...prev, current_value_min: e.target.value }))}
                  />
                  <TextField
                    label="Value Max"
                    type="number"
                    inputProps={{ min: 1 }}
                    value={editedData.current_value_max}
                    onChange={(e) => setEditedData((prev) => ({ ...prev, current_value_max: e.target.value }))}
                  />
                  <TextField
                    label="Notes"
                    multiline
                    minRows={3}
                    value={editedData.notes}
                    onChange={(e) => setEditedData((prev) => ({ ...prev, notes: e.target.value }))}
                  />

                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!isValid()}
                    onClick={() => setConfirmOpen(true)}
                  >
                    Save Changes
                  </Button>
                </Box>
              )}

              <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Confirm Changes</DialogTitle>
                <DialogContent>
                  <ul>
                    {Object.entries(editedData).map(([key, val]) => (
                      <li key={key}>{key}: {String(val)}</li>
                    ))}
                  </ul>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleConfirmSave} variant="contained" disabled={isSaving}>Confirm</Button>
                  <Button onClick={() => setConfirmOpen(false)} disabled={isSaving}>Cancel</Button>
                </DialogActions>
              </Dialog>
            </Box>
        </div>
      </div>
    </>
  );
};

export default AdminEditSingleItem;
